import React, { FC, useState, useEffect, useDeferredValue, Suspense } from 'react';
import {
  Box,
  ButtonAppearance,
  Checkbox,
  TextFieldAppearance,
  TextFieldSize,
  MultiSelectCheckbox,
  Text,
  Icon,
  Combobox,
  AvatarSize,
  theme,
  useToastsDispatch,
  ToastAction,
  ToastType,
} from '@bechtel/gds';
import {
  AssignCommoditiesInput,
  AssignCommodityInput,
  CommodityAssignmentError,
  CommodityStatusCode,
  CrewMember,
  CrewMemberDiscipline,
  CrewMemberList,
  PersonaType,
} from '@bechtel/types';
import { ApolloError } from '@apollo/client';
import { t, Trans } from '@lingui/macro';
import { BoxStyled, ButtonStyled, HeadingStyled, ToolTipStyled } from './styled';
import { BulkRequestProps } from './types';
import {
  PersonaMessages,
  useName,
  ErrorRetry,
  SuspenseLoader,
  ErrorBoundary,
  useAppUser,
  CacheKeys,
} from '@bechtel/client-common';
import { filter, forEach, get, isEqual } from 'lodash';
import { useLingui } from '@lingui/react';
import {
  useCrewMember,
  useWeldProcedures,
  useAssignCommodityCrewMembers,
  AssignCommodityCrewMembersErrorModal,
} from '@bechtel/client-weld-common';
import { NetworkStatus } from '@apollo/client';
import { AvatarRegularStyled } from '~/components/organisms/user-info';
const PersonaItem = ({ item }: any) => {
  const { getFullName } = useName();
  const { i18n } = useLingui();
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Box mr={{ sm: '400' }}>
        <AvatarRegularStyled
          hasImageUrl={item && item.imageUrl ? true : false}
          size={AvatarSize.MEDIUM}
          src={item && item.imageUrl ? item.imageUrl : undefined}
        />
      </Box>
      <Box css={{ width: '100%' }} justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }}>
        <Box flexDirection={{ sm: 'column' }}>
          <HeadingStyled weight="600" css={{ paddingBottom: `${theme.spacings[300]}` }}>
            {getFullName(item.firstName, item.lastName)}
          </HeadingStyled>
          <Text size="300" color="grey.600" transform="capitalize">
            {item?.persona && i18n._(PersonaMessages[item?.persona])}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

const PersonaName = ({ item }: any) => {
  const { getFullName } = useName();
  return <Text weight="400">{item.firstName ? getFullName(item.firstName, item.lastName) : t`Select FWE`}</Text>;
};

const SelectedWPS = ({ selectedItemCount, totalItemCount }: any) => {
  return (
    <Box pl={{ sm: '500' }}>
      <Text size="300" family="graphik" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
        {isEqual(selectedItemCount, totalItemCount)
          ? `${t`All WPS `}`
          : selectedItemCount > 0
          ? `${t`${selectedItemCount} WPS`}`
          : `${t`WPS`}`}
      </Text>
    </Box>
  );
};

const WPSItem = ({ item }: any) => {
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Text size="400" family="graphik" color="grey.1000">
        {item?.procedure}
      </Text>
    </Box>
  );
};

export const BulkRequest: FC<BulkRequestProps> = ({
  selectedWeldIds,
  selectAllWeldList,
  bulkRequest,
  setBulkRequest,
  weldListCount,
  selectItems,
  createBulkRodRequest,
  selectedTab,
  setSelectedWeldItems,
  canAssignFieldWeldEngineer,
  canCreateRodRequest,
}) => {
  const dashboardSelectedUser: CrewMember = JSON.parse(sessionStorage?.getItem(CacheKeys.DashboardSelectedUser));
  const { user } = useAppUser();
  const dispatchToast = useToastsDispatch();
  const [searchText, setSearchText] = useState<string>('');
  const [searchFWEText, setSearchFWEText] = useState<string>('');
  const [selectedWPS, setSelectedWPS] = useState<string[]>([]);
  const [selectedFWE, setSelectedFWE] = useState<CrewMember | null>(null);
  const { getFullName } = useName();
  const itemToString = (item) => getFullName(item.firstName, item.lastName);
  const debouncedSearchWPSText = useDeferredValue(searchText);
  const debouncedSearchFWEText = useDeferredValue(searchFWEText);
  const [actionCompleted, setActionCompleted] = useState<boolean>(false);
  const [selectedWeldCount, setSelectedWeldCount] = useState<number>(0);
  const [assignError, setAssignError] = useState<CommodityAssignmentError[] | ApolloError | null>();
  useEffect(() => {
    setSelectedWeldCount(selectedWeldIds.length);
  }, [selectedWeldIds]);
  const [
    getWeldProceduresById,
    { error: wpsError, data, loading: wpsLoading, networkStatus: wpsNetworkStatus, refetch: wpsRefetch },
  ] = useWeldProcedures();
  useEffect(() => {
    if (debouncedSearchWPSText.length > 0 && debouncedSearchWPSText.length < 3) return;
    getWeldProceduresById({
      variables: {
        input: {
          searchKeyword: debouncedSearchWPSText,
        },
        paging: {
          cursor: '1',
          limit: 15,
        },
      },
    });
  }, [debouncedSearchWPSText, getWeldProceduresById]);

  const weldProceduresList = get(data, 'loadWeldProcedures.items', []);

  const [
    getFWEList,
    { error: fweError, data: fweData, loading: fweLoading, networkStatus: fweNetworkStatus, refetch: fweRefetch },
  ] = useCrewMember(true);
  useEffect(() => {
    if ((debouncedSearchFWEText.length > 0 && debouncedSearchFWEText.length < 3) || !bulkRequest) return;
    getFWEList({
      variables: {
        input: {
          searchKeyword: debouncedSearchFWEText,
          persona: PersonaType.FIELD_WELD_ENGINEER,
        },
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
  }, [debouncedSearchFWEText, getFWEList, bulkRequest]);

  const fweList = get(fweData, 'crewMembers.items', []) as CrewMemberList['items'];

  const [
    assignCommodityCrewMembers,
    { loading: assignFWEloading, error: assignFWEError, data: assignFWEData },
  ] = useAssignCommodityCrewMembers(() => {
    setActionCompleted(true);
  }, [
    'commodities',
    'commodityStats',
    'commodityStatsPriority',
    'commodityWeeklyStats',
    'commodityStatsClaimedAndUnassignedFWE',
    'commodityStatsRejectedFWE',
    'commodityDashboardTabStats',
    'commodityBlocksStatsFWE',
    'assignToMeStatsFWE',
    'commodityFitupStats',
    'commodityStatsAllPriorityStats',
  ]);
  useEffect(() => {
    if (!actionCompleted) return;
    const erroredFWEAssignment =
      get(assignFWEData, 'assignCommodityCrewMembers.erroredCommodityAssignments') || assignFWEError;
    if (erroredFWEAssignment && erroredFWEAssignment?.length == 0) {
      setAssignError(null);
      const assignedFWEToast: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.SUCCESS,
          content: `${t`Field Weld Engineer assigned successfully.`}`,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(assignedFWEToast);
      setSelectedWeldCount(0);
      setSelectedFWE(null);
      setSelectedWeldItems([]);
    } else {
      setAssignError(erroredFWEAssignment);
    }
    setActionCompleted(false);
  }, [actionCompleted]);
  const closedUnassignModal = () => {
    setAssignError(null);
  };
  const loggedUserId = dashboardSelectedUser?.id ? dashboardSelectedUser?.id : user?.crewMember?.id;
  const filteredFWEList = filter(fweList, (fwe) => fwe.id !== loggedUserId);

  const resetBulkRequest = () => {
    setBulkRequest(false);
    setSelectedWPS([]);
    setSelectedFWE(null);
    selectItems(false);
  };

  if (canAssignFieldWeldEngineer && selectedTab === CommodityStatusCode.CLAIMED && !!weldListCount) {
    return (
      <BoxStyled data-testid="bulk-assign">
        {bulkRequest ? (
          <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'space-between' }} flexGrow={{ sm: 1 }}>
            <Box alignItems={{ sm: 'center' }}>
              <Checkbox
                onChange={() => selectAllWeldList()}
                css={!selectedFWE?.id.length ? { opacity: 0.4, pointerEvents: 'none' } : ''}
                indeterminate={selectedFWE !== null && selectedWeldCount > 0 && selectedWeldCount < weldListCount}
                checked={selectedFWE !== null && selectedWeldCount === weldListCount && selectedWeldCount > 0}
              />
              <Box mx={{ sm: '400' }} css={!selectedFWE ? { opacity: 0.5 } : ''}>
                <Text color="grey.1000" size="300">
                  <Trans>Select All</Trans> ({weldListCount})
                </Text>
              </Box>
              <Box style={{ width: '200px', position: 'relative' }}>
                {!fweLoading && fweError && (
                  <ErrorRetry refetch={fweRefetch}>
                    <Trans>There was a problem loading Field weld Engineers</Trans>
                  </ErrorRetry>
                )}
                {!fweError && (
                  <Combobox
                    ariaLabel="select-fwe"
                    loading={fweLoading || fweNetworkStatus === NetworkStatus.refetch}
                    size={TextFieldSize.SMALL}
                    items={filteredFWEList}
                    itemComponent={PersonaItem}
                    selectedComponent={PersonaName}
                    term={searchFWEText}
                    setTerm={setSearchFWEText}
                    placeholder={t`Select FWE`}
                    appearance={TextFieldAppearance.NORMAL}
                    itemString={itemToString}
                    selectedItem={selectedFWE !== null ? selectedFWE : t`Select FWE`}
                    onSelect={(item: CrewMember) => {
                      setSelectedFWE(item);
                      selectItems(!selectedFWE !== null ? true : false);
                    }}
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setSearchFWEText(event.target.value);
                    }}
                    messageOnNoResults={t`No FWE found`}
                    restorePreviousOnSkip
                  ></Combobox>
                )}

                {!selectedFWE?.id.length && (
                  <ToolTipStyled css={{ right: '-175px' }}>
                    <Text color="white" size="300">
                      <Trans>Please select FWE to assign</Trans>
                    </Text>
                  </ToolTipStyled>
                )}
              </Box>
            </Box>

            <Box alignItems={{ sm: 'center' }}>
              {selectedWeldCount > 0 ? (
                <Box>
                  <Text color="grey.1000" size="400" weight="600">
                    {selectedWeldCount}
                  </Text>
                  &nbsp;
                  <Text color="grey.600" size="400">
                    {selectedWeldCount > 1 ? t`Items Selected` : t`Item Selected`}
                  </Text>
                </Box>
              ) : null}
              <ButtonStyled
                appearance={ButtonAppearance.PRIMARY}
                css={{ width: 'auto' }}
                disabled={!selectedFWE?.id || !selectedWeldCount}
                onClick={() => {
                  const fieldWeldEngineerInput: AssignCommodityInput[] = [];
                  forEach(selectedWeldIds, (selectedWeldId) => {
                    fieldWeldEngineerInput.push({
                      commodityId: selectedWeldId,
                      crewMemberId: selectedFWE?.id,
                      personaType: PersonaType.FIELD_WELD_ENGINEER,
                    });
                  });

                  const input: AssignCommoditiesInput = {
                    input: fieldWeldEngineerInput,
                  };
                  assignCommodityCrewMembers({
                    variables: { input },
                  });
                }}
              >
                <Icon icon="user-plus" fillColor="white"></Icon>&nbsp;<Trans>Assign Selected</Trans>
              </ButtonStyled>
              &nbsp;&nbsp;
              <ButtonStyled
                appearance={ButtonAppearance.SECONDARY}
                disabled={assignFWEloading}
                onClick={resetBulkRequest}
              >
                <Trans>Cancel</Trans>
              </ButtonStyled>
            </Box>
          </Box>
        ) : (
          <ButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={() => setBulkRequest(true)}>
            <Trans>Bulk Assign</Trans>
          </ButtonStyled>
        )}
        {(assignError instanceof ApolloError || (assignError instanceof Array && assignError?.length > 0)) && (
          <ErrorBoundary>
            <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
              <AssignCommodityCrewMembersErrorModal
                commodityAssignmentErrors={assignError}
                close={closedUnassignModal}
              />
            </Suspense>
          </ErrorBoundary>
        )}
      </BoxStyled>
    );
  }
  if (canCreateRodRequest && selectedTab === CommodityStatusCode.READY_TO_WELD) {
    return (
      <>
        {bulkRequest && (
          <BoxStyled data-testid="bulk-assign">
            <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'space-between' }} flexGrow={{ sm: 1 }}>
              <Box alignItems={{ sm: 'center' }}>
                <Checkbox
                  onChange={() => selectAllWeldList()}
                  css={selectedWPS.length === 0 ? { opacity: 0.4, pointerEvents: 'none' } : ''}
                  indeterminate={selectedWPS.length > 0 && selectedWeldCount > 0 && selectedWeldCount < weldListCount}
                  checked={selectedWPS.length > 0 && selectedWeldCount === weldListCount && selectedWeldCount > 0}
                />
                <Box mx={{ sm: '400' }} css={selectedWPS.length === 0 ? { opacity: 0.5 } : ''}>
                  <Text color="grey.1000" size="300">
                    <Trans>Select All</Trans> ({weldListCount})
                  </Text>
                </Box>
                <Box style={{ width: '130px', position: 'relative' }}>
                  {!wpsLoading && wpsError && (
                    <div data-testid="welder-error">
                      <ErrorRetry refetch={wpsRefetch}>
                        <Trans>There was a problem loading Weld Procedures</Trans>
                      </ErrorRetry>
                    </div>
                  )}
                  {!wpsError && (
                    <MultiSelectCheckbox
                      loading={wpsLoading || wpsNetworkStatus === NetworkStatus.refetch}
                      searchPlaceholder={t`Search WPS`}
                      showSearchBar={true}
                      autoPosition={false}
                      onSearchInputChange={(text) => {
                        if (text.length > 0 && text.length < 3) return;
                        setSearchText(text);
                      }}
                      term={searchText}
                      setTerm={setSearchText}
                      items={weldProceduresList}
                      itemString={itemToString}
                      itemComponent={WPSItem}
                      selectedComponent={SelectedWPS}
                      size={TextFieldSize.SMALL}
                      showSelectAll={true}
                      confirmSelection={true}
                      appearance={TextFieldAppearance.NORMAL}
                      displayKeyField="procedure"
                      placeholder={t`All WPS`}
                      onSelectedItemsChange={(selectedItems) => {
                        selectAllWeldList(true);
                        setSelectedWPS(selectedItems);
                        selectItems(!selectedWPS.length);
                      }}
                    />
                  )}
                  {!selectedWPS.length && (
                    <ToolTipStyled>
                      <Text color="white" size="300">
                        <Trans>Please Select WPS</Trans>
                      </Text>
                    </ToolTipStyled>
                  )}
                </Box>
              </Box>

              <Box alignItems={{ sm: 'center' }}>
                {selectedWeldCount > 0 ? (
                  <Box>
                    <Text color="grey.1000" size="400" weight="600">
                      {selectedWeldCount}
                    </Text>
                    &nbsp;
                    <Text color="grey.600" size="400">
                      {selectedWeldCount > 1 ? t`Items Selected` : t`Item Selected`}
                    </Text>
                  </Box>
                ) : null}
                <ButtonStyled
                  onClick={() => {
                    if (!createBulkRodRequest) return;
                    createBulkRodRequest();
                    resetBulkRequest();
                  }}
                  appearance={ButtonAppearance.PRIMARY}
                  css={{ width: 'auto' }}
                  disabled={!selectedWPS?.length || !selectedWeldCount}
                >
                  <Icon icon="file-spreadsheet" fillColor="white"></Icon>&nbsp;
                  {selectedWeldCount > 0 ? t`Request Selected` : t`Rod Request`}
                </ButtonStyled>
                &nbsp;&nbsp;
                <ButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={resetBulkRequest}>
                  <Trans>Cancel</Trans>
                </ButtonStyled>
              </Box>
            </Box>
          </BoxStyled>
        )}

        {canCreateRodRequest && !!weldListCount && !bulkRequest && (
          <BoxStyled data-testid="bulk-assign">
            <ButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={() => setBulkRequest(true)}>
              <Trans>Bulk Rod Request</Trans>
            </ButtonStyled>
          </BoxStyled>
        )}
      </>
    );
  }
  return null;
};
